import { router, usePage } from '@inertiajs/react'
import TaskLogForm, {
  type ExistingTaskLog,
  type LotOptionRow,
  type LoggerOption,
  type ComplianceWindow,
  type LotValidityWarning,
} from '@/features/task-logging/TaskLogForm'
import { Button } from '@/shared/ui/button'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  historyInstrumentTaskTaskLogPath,
  instrumentPath,
  instrumentsPath,
} from '@/shared/lib/routes'
import type WebInstrumentsInstrumentRef from '@/shared/types/web/WebInstrumentsInstrumentRef'
import type WebInstrumentsInstrumentTaskRef from '@/shared/types/web/WebInstrumentsInstrumentTaskRef'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'
import type { SharedProps } from '@/shared/types/SharedProps'

type TaskLogPageProps = {
  mode: 'new'
  instrument: WebInstrumentsInstrumentRef
  instrumentTask: WebInstrumentsInstrumentTaskRef
  taskSelection: { id: number } | null
  dataFields: WebInstrumentsTaskLogDataField[]
  lotOptions: LotOptionRow[]
  linkedLotId?: number | null
  linkedLotNoInventory?: boolean
  lotValidityWarnings?: LotValidityWarning[]
  loggerOptions: LoggerOption[]
  logAuthenticationCode: string | null
  complianceWindow: ComplianceWindow | null
  existingTaskLog: ExistingTaskLog | null
  formAction: string
  formMethod: 'post' | 'patch'
  prefillCompletedAt?: string | null
  openMissTime?: string | null
  siteTimeZone?: string | null
  dayOff?: boolean
  scheduleLevel?: string | null
  errors?: Record<string, string | string[]>
}

const TaskLogPage = () => {
  const { props: page } = usePage<SharedProps & TaskLogPageProps>()

  const { instrument, instrumentTask, formAction, formMethod, taskHistoryEnabled } = page
  const taskLogHistoryPath = historyInstrumentTaskTaskLogPath(instrument.id, instrumentTask.id)
  // Task History is hidden until the business defines it (REBUILD-212). While it is off, the
  // History breadcrumb is dropped and Cancel falls back to the instrument's Tasks tab.
  const tasksTabPath = `${instrumentPath(instrument.id)}#tasks`
  const cancelPath = taskHistoryEnabled ? taskLogHistoryPath : tasksTabPath
  useBreadcrumbs([
    { label: t('nav.instruments'), href: instrumentsPath() },
    { label: instrument.instrumentLabel, href: instrumentPath(instrument.id) },
    { label: t('instruments.show.tabs.tasks'), href: tasksTabPath },
    ...(taskHistoryEnabled ? [{ label: 'History', href: taskLogHistoryPath }] : []),
    { label: 'Log' },
  ])

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold">{t('instruments.taskLogs.titleNew')}</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {instrument.instrumentLabel} · {instrumentTask.name}
        </p>
      </div>

      <TaskLogForm
        mode={page.mode}
        dataFields={page.dataFields}
        lotOptions={page.lotOptions}
        linkedLotId={page.linkedLotId}
        linkedLotNoInventory={page.linkedLotNoInventory}
        lotValidityWarnings={page.lotValidityWarnings}
        loggerOptions={page.loggerOptions}
        logAuthenticationCode={page.logAuthenticationCode}
        complianceWindow={page.complianceWindow}
        existingTaskLog={page.existingTaskLog}
        formAction={formAction}
        formMethod={formMethod}
        prefillCompletedAt={page.prefillCompletedAt}
        openMissTime={page.openMissTime}
        siteTimeZone={page.siteTimeZone}
        instrumentId={instrument.id}
        dayOff={page.dayOff}
        scheduleLevel={page.scheduleLevel}
        errors={page.errors}
      />

      <div className="pt-2 border-t">
        <Button type="button" variant="outline" onClick={() => router.visit(cancelPath)}>
          {t('instruments.taskLogs.cancel')}
        </Button>
      </div>
    </div>
  )
}

export default TaskLogPage
